import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { chatbotAdminService } from '@/api/chatbot-admin';
import type { LogsStats, LogsStatsParams } from '@/types/chatbot-admin';
import type { SuperAdminCabinet } from '@/types/super-admin';
import { superAdminService } from '@/api/super-admin';
import { 
  IconChartBar, 
  IconClock, 
  IconMessages, 
  IconPercentage, 
  IconRobot,
  IconTools,
  IconTrendingUp,
  IconAlertCircle
} from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/date-range-picker';
import type { DateRange } from 'react-day-picker';
import { subDays, subMonths, subYears, startOfDay, endOfDay } from 'date-fns';

interface LogsStatsDashboardProps {
  initialCabinet?: string;
}

type DateFilterPreset = 'last_day' | 'last_month' | 'last_year' | 'custom' | 'all';

export function LogsStatsDashboard({ initialCabinet }: LogsStatsDashboardProps) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<LogsStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cabinet, setCabinet] = useState<string>(initialCabinet || '');
  const [cabinets, setCabinets] = useState<SuperAdminCabinet[]>([]);
  const [datePreset, setDatePreset] = useState<DateFilterPreset>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  useEffect(() => {
    const fetchCabinets = async () => {
      try {
        const data = await superAdminService.getCabinets();
        const list = Array.isArray(data) ? data : (data as any)?.results || [];
        setCabinets(list);
      } catch (err) {
        setCabinets([]);
      }
    };
    fetchCabinets();
  }, []);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinet, datePreset, dateRange]);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: LogsStatsParams = {};
      
      if (cabinet) {
        params.cabinet = cabinet;
      }

      // Handle date filtering
      if (datePreset === 'last_day') {
        const startDate = startOfDay(subDays(new Date(), 1));
        params.start_date = startDate.toISOString();
        params.end_date = new Date().toISOString();
      } else if (datePreset === 'last_month') {
        const startDate = startOfDay(subMonths(new Date(), 1));
        params.start_date = startDate.toISOString();
        params.end_date = new Date().toISOString();
      } else if (datePreset === 'last_year') {
        const startDate = startOfDay(subYears(new Date(), 1));
        params.start_date = startDate.toISOString();
        params.end_date = new Date().toISOString();
      } else if (datePreset === 'custom' && dateRange?.from) {
        params.start_date = startOfDay(dateRange.from).toISOString();
        if (dateRange.to) {
          params.end_date = endOfDay(dateRange.to).toISOString();
        }
      }

      const data = await chatbotAdminService.getLogsStats(params);
      setStats(data);
    } catch (e: any) {
      setError(e.message || t('failed_to_load_stats'));
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (preset: DateFilterPreset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      setDateRange(undefined);
    }
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from) {
      setDatePreset('custom');
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const formatDecimal = (num: number, decimals = 2) => {
    return num.toFixed(decimals);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">{t('loading_stats')}...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-destructive">
            <IconAlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconChartBar className="w-5 h-5" />
            {t('logs_statistics_dashboard')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            {/* Cabinet Filter */}
            <Select value={cabinet || 'all'} onValueChange={(v) => setCabinet(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder={t('cabinet')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_cabinets')}</SelectItem>
                {cabinets.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Preset Filter */}
            <Select value={datePreset} onValueChange={(v) => handlePresetChange(v as DateFilterPreset)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('date_filter')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_time')}</SelectItem>
                <SelectItem value="last_day">{t('last_day')}</SelectItem>
                <SelectItem value="last_month">{t('last_month')}</SelectItem>
                <SelectItem value="last_year">{t('last_year')}</SelectItem>
                <SelectItem value="custom">{t('custom_range')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom Date Range Picker */}
            {datePreset === 'custom' && (
              <DateRangePicker
                date={dateRange}
                onDateChange={handleDateRangeChange}
              />
            )}

            <Button variant="outline" size="sm" onClick={fetchStats}>
              {t('refresh')}
            </Button>
          </div>

          {/* Period Display */}
          {stats.period && (stats.period.start || stats.period.end) && (
            <div className="mt-3 text-sm text-muted-foreground">
              {t('period')}: {stats.period.start ? new Date(stats.period.start).toLocaleDateString() : t('beginning')} - {stats.period.end ? new Date(stats.period.end).toLocaleDateString() : t('now')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Totals and Rates */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('total_logs')}</CardTitle>
            <IconMessages className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(stats.totals.logs)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('total_messages_logged')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('total_tokens')}</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(stats.totals.tokens)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('total_tokens_consumed')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('success_rate')}</CardTitle>
            <IconPercentage className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDecimal(stats.rates.success_rate_percent)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(stats.rates.success_count)} {t('successful')} / {formatNumber(stats.rates.error_count)} {t('errors')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('avg_tokens_per_message')}</CardTitle>
            <IconChartBar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(stats.averages.tokens_per_message)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('average_token_usage')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Averages */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('avg_llm_response_time')}</CardTitle>
            <IconClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(stats.averages.llm_response_time_ms)} ms</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('llm_processing_time')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('avg_processing_time')}</CardTitle>
            <IconClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(stats.averages.processing_time_ms)} ms</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('total_processing_time')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('avg_tool_calls')}</CardTitle>
            <IconTools className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDecimal(stats.averages.tool_calls_per_message)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('avg_tools_per_message')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Models and Tools */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Top Models */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconRobot className="w-5 h-5" />
              {t('top_models')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_models.length === 0 ? (
              <div className="text-sm text-muted-foreground">{t('no_data_available')}</div>
            ) : (
              <div className="space-y-3">
                {stats.top_models.map((model, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Badge variant="outline" className="shrink-0">
                        #{idx + 1}
                      </Badge>
                      <span className="text-sm truncate" title={model.model}>
                        {model.model}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-medium">{formatNumber(model.count)}</span>
                      <Badge variant="secondary">
                        {formatDecimal(model.percentage)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Tools */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconTools className="w-5 h-5" />
              {t('top_tools')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_tools.length === 0 ? (
              <div className="text-sm text-muted-foreground">{t('no_data_available')}</div>
            ) : (
              <div className="space-y-3">
                {stats.top_tools.map((tool, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Badge variant="outline" className="shrink-0">
                        #{idx + 1}
                      </Badge>
                      <span className="text-sm truncate" title={tool.tool}>
                        {tool.tool}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-medium">{formatNumber(tool.count)}</span>
                      <Badge variant="secondary">
                        {formatDecimal(tool.percentage)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
